/**
 * Service-worker registration for the installed PWA.
 *
 * vite-plugin-pwa (registerType 'autoUpdate') emits a worker that calls
 * skipWaiting() and clientsClaim(), so a newly installed worker takes control
 * of every open page as soon as it activates. Taking control does not reload
 * the page, though: the bundle already in memory keeps running, and on iOS a
 * home-screen app keeps its own storage, so the old build could survive
 * indefinitely. This module reloads the page exactly once when control passes
 * from one worker to another, so a deploy reaches a running client without
 * the user deleting and re-adding the icon.
 *
 * Termination: `reloadRequested` is module scope and is set before reload()
 * is called, so a second `controllerchange` in the same document is ignored.
 * The reload produces a new document controlled by the newest worker; that
 * document only reloads again if a byte-different sw.js is installed, which
 * needs another deploy. The sequence is therefore bounded by deploys, not by
 * events.
 */

const SW_URL = '/sw.js';
const SW_SCOPE = '/';

/** The subset of ServiceWorkerContainer this module touches — injectable for tests. */
export interface ServiceWorkerContainerLike {
  readonly controller: object | null;
  register(scriptURL: string, options?: { scope?: string }): Promise<unknown>;
  addEventListener(type: 'controllerchange', listener: () => void): void;
}

/** The subset of Location this module touches — injectable for tests. */
export interface LocationLike {
  reload(): void;
}

/** One-shot guard. Set before the reload is requested; never cleared within a document. */
let reloadRequested = false;

/**
 * Attach the takeover handler and register the worker.
 *
 * Reloads only when the page was already controlled by a worker before the
 * event: on a first install there is no old bundle to replace, and reloading
 * there would just cost the user a flash on their first visit.
 */
export function registerServiceWorker(
  container: ServiceWorkerContainerLike,
  location: LocationLike
): void {
  let hadController = container.controller !== null;

  container.addEventListener('controllerchange', () => {
    const takeover = hadController;
    hadController = true;
    if (!takeover) return;
    if (reloadRequested) return;
    reloadRequested = true;
    location.reload();
  });

  container.register(SW_URL, { scope: SW_SCOPE }).catch(() => {
    // A failed registration leaves the app usable from the network; nothing to do here.
  });
}

/** Entry point for main.tsx: no-op where service workers are unavailable (e.g. plain http). */
export function registerServiceWorkerOnLoad(): void {
  if (!('serviceWorker' in navigator)) return;
  const start = () => registerServiceWorker(navigator.serviceWorker, window.location);
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}
